// Main JavaScript Entry Point for Fortisys Website
// Based on Technical Specification Section 4.2

// Import styles
import '../styles/main.scss';
// Import icons
import './icons.js';
// Import components
import { Navigation } from './components/Navigation.js';
import { MobileMenu } from './components/MobileMenu.js';
import { FormValidator } from './components/FormValidator.js';
import { ScrollManager } from './utils/ScrollManager.js';
import { LazyLoader } from './utils/LazyLoader.js';
import { ButtonManager } from './utils/ButtonManager.js';
import { AccessibilityManager } from './utils/AccessibilityManager.js';
import { PerformanceManager } from './utils/PerformanceManager.js';

// Application class for managing the entire website
class FortisysApp {
  constructor() {
    this.components = new Map();
    this.isInitialized = false;
    this.init();
  }

  async init() {
    try {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        await new Promise(resolve => {
          document.addEventListener('DOMContentLoaded', resolve);
        });
      }

      // Initialize core utilities
      this.initUtilities();

      // Initialize components
      this.initComponents();

      // Set up global event listeners
      this.bindGlobalEvents();

      // Setup enhanced CTA buttons
      this.setupCTAButtons();

      this.isInitialized = true;
      console.log('Fortisys website initialized successfully');
    } catch (error) {
      console.error('Error initializing Fortisys website:', error);
    }
  }

  initUtilities() {
    // Initialize scroll manager for header behavior and smooth scrolling
    this.scrollManager = new ScrollManager();

    // Initialize lazy loading for images and content
    this.lazyLoader = new LazyLoader({
      rootMargin: '50px',
      threshold: 0.1
    });

    // Initialize button manager for interactions and states
    this.buttonManager = new ButtonManager({
      rippleEffect: true,
      loadingDuration: 300
    });

    // Inject button styles
    this.buttonManager.injectStyles();

    // Initialize accessibility manager
    this.accessibilityManager = new AccessibilityManager({
      enableFocusManagement: true,
      enableLiveRegions: true,
      enableSkipLinks: true,
      announcePageChanges: true
    });

    // Initialize performance manager
    this.performanceManager = new PerformanceManager({
      enableMetrics: true,
      enableErrorHandling: true,
      enableResourceHints: true,
      reportingEndpoint: null // Configure with analytics endpoint if needed
    });
  }

  initComponents() {
    try {
      // Initialize navigation
      const headerElement = document.querySelector('.header');
      if (headerElement) {
        const navigation = new Navigation(headerElement);
        this.components.set('navigation', navigation);
      }

      // Initialize mobile menu
      const mobileToggle = document.querySelector('.header__mobile-toggle');
      const mobileMenu = document.querySelector('.header__mobile-menu');
      if (mobileToggle && mobileMenu) {
        const mobileMenuComponent = new MobileMenu(mobileToggle, mobileMenu);
        this.components.set('mobileMenu', mobileMenuComponent);
      }

      // Initialize forms
      const forms = document.querySelectorAll('form');
      forms.forEach((form, index) => {
        const formValidator = new FormValidator(form);
        this.components.set(`form-${index}`, formValidator);

        // Enhance form submission with button loading states
        this.enhanceFormSubmission(form, formValidator);
      });

      // Initialize page-specific components
      this.initPageSpecificComponents();
    } catch (error) {
      console.error('Error initializing components:', error);
    }
  }

  initPageSpecificComponents() {
    const currentPage = this.getCurrentPage();

    switch (currentPage) {
    case 'home':
      this.initHomePageComponents();
      break;
    case 'platform':
      this.initPlatformPageComponents();
      break;
    case 'solutions':
      this.initSolutionsPageComponents();
      break;
    case 'contact':
      this.initContactPageComponents();
      break;
    default:
      // Generic page initialization
      break;
    }
  }

  initHomePageComponents() {
    // Initialize hero animations or interactions
    const heroSection = document.querySelector('.hero');
    if (heroSection) {
      this.initHeroInteractions(heroSection);
    }

    // Initialize value props animations
    const valuePropsSection = document.querySelector('.value-props');
    if (valuePropsSection) {
      this.initScrollAnimations(valuePropsSection);
    }
  }

  initPlatformPageComponents() {
    // Platform-specific component initialization
    const platformOverview = document.querySelector('.platform-overview');
    if (platformOverview) {
      this.initScrollAnimations(platformOverview);
    }
  }

  initSolutionsPageComponents() {
    // Solutions-specific component initialization
    const solutionsShowcase = document.querySelector('.solutions-showcase');
    if (solutionsShowcase) {
      this.initScrollAnimations(solutionsShowcase);
    }
  }

  initContactPageComponents() {
    // Contact-specific component initialization
    // Additional form enhancements would go here
  }

  initHeroInteractions(heroElement) {
    // Add any hero-specific interactions
    // This could include parallax effects, animations, etc.
    const heroImage = heroElement.querySelector('.hero__image');
    if (heroImage) {
      // Ensure hero image is loaded properly
      if (heroImage.complete) {
        heroElement.classList.add('hero--loaded');
      } else {
        heroImage.addEventListener('load', () => {
          heroElement.classList.add('hero--loaded');
        });
      }
    }
  }

  initScrollAnimations(element) {
    // Add intersection observer for scroll-triggered animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    observer.observe(element);
  }

  bindGlobalEvents() {
    // Handle resize events
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 150);
    });

    // Handle focus management for accessibility
    this.setupFocusManagement();

    // Handle external links
    this.setupExternalLinks();
  }

  handleResize() {
    // Notify components of resize
    this.components.forEach(component => {
      if (typeof component.handleResize === 'function') {
        component.handleResize();
      }
    });
  }

  setupFocusManagement() {
    // Ensure keyboard navigation works properly
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.body.classList.add('using-keyboard');
      }
    });

    document.addEventListener('mousedown', () => {
      document.body.classList.remove('using-keyboard');
    });
  }

  setupExternalLinks() {
    // Add appropriate attributes to external links
    const externalLinks = document.querySelectorAll('a[href^="http"]:not([href*="fortisys.com"])');
    externalLinks.forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
  }

  getCurrentPage() {
    const path = window.location.pathname;

    if (path === '/' || path === '/index.html') {
      return 'home';
    }

    if (path.startsWith('/platform')) {
      return 'platform';
    }

    if (path.startsWith('/solutions')) {
      return 'solutions';
    }

    if (path.startsWith('/contact')) {
      return 'contact';
    }

    if (path.startsWith('/company')) {
      return 'company';
    }

    if (path.startsWith('/resources')) {
      return 'resources';
    }

    return 'other';
  }

  // Public API methods
  getComponent(name) {
    return this.components.get(name);
  }

  isReady() {
    return this.isInitialized;
  }

  destroy() {
    // Clean up all components
    this.components.forEach(component => {
      if (typeof component.destroy === 'function') {
        component.destroy();
      }
    });

    this.components.clear();

    // Clean up utilities
    if (this.scrollManager) {
      this.scrollManager.destroy();
    }

    if (this.lazyLoader) {
      this.lazyLoader.destroy();
    }

    // Clean up button manager
    if (this.buttonManager) {
      this.buttonManager.destroy();
    }

    // Clean up accessibility manager
    if (this.accessibilityManager) {
      this.accessibilityManager.destroy();
    }

    // Clean up performance manager
    if (this.performanceManager) {
      this.performanceManager.destroy();
    }

    this.isInitialized = false;
  }

  enhanceFormSubmission(form, validator) {
    // Listen for form submission events and handle button states
    form.addEventListener('formValidSubmit', (e) => {
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) {
        this.buttonManager.setLoading(submitButton, true, 'Submitting...');
      }
    });

    form.addEventListener('formSubmitSuccess', (e) => {
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) {
        this.buttonManager.setLoading(submitButton, false);
        this.buttonManager.animateSuccess(submitButton);
      }
    });

    form.addEventListener('formSubmitError', (e) => {
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) {
        this.buttonManager.setLoading(submitButton, false);
        this.buttonManager.animateError(submitButton);
      }
    });
  }

  // Enhanced CTA button handlers
  setupCTAButtons() {
    const demoButtons = document.querySelectorAll('.btn--demo');
    demoButtons.forEach(button => {
      // Skip if it's a submit button, let the form handler take over
      if (button.getAttribute('type') === 'submit') return;

      button.addEventListener('click', (e) => {
        if (button.getAttribute('href')) return; // Let links work normally

        e.preventDefault();
        this.buttonManager.handleClick(button, () => {
          // Simulate demo request process
          this.handleDemoRequest(button);
        });
      });
    });

    const ctaButtons = document.querySelectorAll('.btn--primary, .btn--secondary');
    ctaButtons.forEach(button => {
      if (!button.getAttribute('href')) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.buttonManager.handleClick(button, () => {
            this.handleCTAClick(button);
          });
        });
      }
    });
  }

  async handleDemoRequest(button) {
    this.buttonManager.setLoading(button, true, 'Processing...');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Success animation
      this.buttonManager.setLoading(button, false);
      this.buttonManager.animateSuccess(button);

      // Redirect or show success message
      setTimeout(() => {
        window.location.href = '/contact/demo';
      }, 1000);
    } catch (error) {
      this.buttonManager.setLoading(button, false);
      this.buttonManager.animateError(button);
    }
  }

  handleCTAClick(button) {
    // Add click analytics or other actions
    const href = button.dataset.href || button.closest('a')?.href;
    if (href) {
      window.location.href = href;
    }
  }
}

// Initialize the application
const app = new FortisysApp();

// Make app available globally for debugging
if (typeof window !== 'undefined') {
  window.FortisysApp = app;
}

// Export for module systems
export default FortisysApp;
